import _toConsumableArray from "/root/workspace/sportal365-cms_release-3.242.0/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/toConsumableArray";

/**
 * Common functions and function call helpers.
 *
 * @module function
 */
import * as array from './array.js';
import * as object from './object.js';
/**
 * Calls all functions in `fs` with args. Only throws after all functions were called.
 *
 * @param {Array<function>} fs
 * @param {Array<any>} args
 */

export var callAll = function callAll(fs, args) {
  var i = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;

  try {
    for (; i < fs.length; i++) {
      fs[i].apply(fs, _toConsumableArray(args));
    }
  } finally {
    if (i < fs.length) {
      callAll(fs, args, i + 1);
    }
  }
};
export var nop = function nop() {};
/**
 * @template T
 * @param {function():T} f
 * @return {T}
 */

export var apply = function apply(f) {
  return f();
};
/**
 * @template A
 *
 * @param {A} a
 * @return {A}
 */

export var id = function id(a) {
  return a;
};
/**
 * @template T
 *
 * @param {T} a
 * @param {T} b
 * @return {boolean}
 */

export var equalityStrict = function equalityStrict(a, b) {
  return a === b;
};
/**
 * @template T
 *
 * @param {Array<T>|object} a
 * @param {Array<T>|object} b
 * @return {boolean}
 */

export var equalityFlat = function equalityFlat(a, b) {
  return a === b || a != null && b != null && a.constructor === b.constructor && (array.isArray(a) && array.equalFlat(a,
  /** @type {Array<T>} */
  b) || typeof a === 'object' && object.equalFlat(a, b));
};
/* c8 ignore start */

/**
 * @param {any} a
 * @param {any} b
 * @return {boolean}
 */

export var equalityDeep = function equalityDeep(a, b) {
  if (a == null || b == null) {
    return equalityStrict(a, b);
  }

  if (a.constructor !== b.constructor) {
    return false;
  }

  if (a === b) {
    return true;
  }

  switch (a.constructor) {
    case ArrayBuffer:
      a = new Uint8Array(a);
      b = new Uint8Array(b);
    // eslint-disable-next-line no-fallthrough

    case Uint8Array:
      {
        if (a.byteLength !== b.byteLength) {
          return false;
        }

        for (var i = 0; i < a.length; i++) {
          if (a[i] !== b[i]) {
            return false;
          }
        }

        break;
      }

    case Set:
      {
        if (a.size !== b.size) {
          return false;
        }

        var _iteratorNormalCompletion = true;
        var _didIteratorError = false;
        var _iteratorError = undefined;

        try {
          for (var _iterator = a[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
            var value = _step.value;

            if (!b.has(value)) {
              return false;
            }
          }
        } catch (err) {
          _didIteratorError = true;
          _iteratorError = err;
        } finally {
          try {
            if (!_iteratorNormalCompletion && _iterator.return != null) {
              _iterator.return();
            }
          } finally {
            if (_didIteratorError) {
              throw _iteratorError;
            }
          }
        }

        break;
      }

    case Map:
      {
        if (a.size !== b.size) {
          return false;
        }

        var _iteratorNormalCompletion2 = true;
        var _didIteratorError2 = false;
        var _iteratorError2 = undefined;

        try {
          for (var _iterator2 = a.keys()[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
            var key = _step2.value;

            if (!b.has(key) || !equalityDeep(a.get(key), b.get(key))) {
              return false;
            }
          }
        } catch (err) {
          _didIteratorError2 = true;
          _iteratorError2 = err;
        } finally {
          try {
            if (!_iteratorNormalCompletion2 && _iterator2.return != null) {
              _iterator2.return();
            }
          } finally {
            if (_didIteratorError2) {
              throw _iteratorError2;
            }
          }
        }

        break;
      }

    case Object:
      if (object.length(a) !== object.length(b)) {
        return false;
      }

      for (var _key in a) {
        if (!object.hasProperty(a, _key) || !equalityDeep(a[_key], b[_key])) {
          return false;
        }
      }

      break;

    case Array:
      if (a.length !== b.length) {
        return false;
      }

      for (var _i = 0; _i < a.length; _i++) {
        if (!equalityDeep(a[_i], b[_i])) {
          return false;
        }
      }

      break;

    default:
      return false;
  }

  return true;
};
/**
 * @template V
 * @template {V} OPTS
 *
 * @param {V} value
 * @param {Array<OPTS>} options
 */
// @ts-ignore

export var isOneOf = function isOneOf(value, options) {
  return options.includes(value);
};
/* c8 ignore stop */

export var isArray = array.isArray;
/**
 * @param {any} s
 * @return {s is String}
 */

export var isString = function isString(s) {
  return s && s.constructor === String;
};
/**
 * @param {any} n
 * @return {n is Number}
 */

export var isNumber = function isNumber(n) {
  return n != null && n.constructor === Number;
};
/**
 * @template {abstract new (...args: any) => any} TYPE
 * @param {any} n
 * @param {TYPE} T
 * @return {n is InstanceType<TYPE>}
 */

export var is = function is(n, T) {
  return n && n.constructor === T;
};
/**
 * @template {abstract new (...args: any) => any} TYPE
 * @param {TYPE} T
 */

export var isTemplate = function isTemplate(T) {
  return (
    /**
     * @param {any} n
     * @return {n is InstanceType<TYPE>}
     **/
    function (n) {
      return n && n.constructor === T;
    }
  );
};